import React, { useState, useEffect, useCallback } from 'react';
import { X, ZoomIn, ChevronLeft, ChevronRight, Image as ImageIcon, PlayCircle } from 'lucide-react';
import { api } from '../config/api';

const categories = [
  { key: 'all', label: 'All', icon: '✦' },
  { key: 'food', label: 'Food', icon: '🍖' },
  { key: 'restaurant', label: 'Restaurant', icon: '🏠' },
  { key: 'events', label: 'Events', icon: '🎉' },
  { key: 'kitchen', label: 'Kitchen', icon: '👨‍🍳' },
  { key: 'desserts', label: 'Desserts', icon: '🍰' },
];

const categoryMap = {
  'Signature': 'food', 'Grill': 'food', 'Seafood': 'food', 'Street Food': 'food',
  'Interior': 'restaurant', 'Kitchen': 'kitchen', 'Dessert': 'desserts', 'Events': 'events',
  'General': 'food'
};

const Gallery = () => {
  const [lightbox, setLightbox] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    fetchGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await api.get('/gallery.php');
      if (res.data.success && res.data.data.length > 0) {
        setImages(res.data.data);
      } else {
        setImages(fallbackImages);
      }
    } catch (err) {
      setImages(fallbackImages);
    } finally {
      setLoading(false);
    }
  };

  const fallbackImages = [
    { title: 'Flame Grilled Chicken', category: 'Signature', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1598515214211-89d3c73ae83b?w=800&q=80' },
    { title: 'Succulent Kebabs', category: 'Grill', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1529006557810-274b9b2fc783?w=800&q=80' },
    { title: 'Fresh Fish & Chips', category: 'Seafood', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1580217593608-61931cefc821?w=800&q=80' },
    { title: 'Restaurant Ambiance', category: 'Interior', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80' },
    { title: 'Gourmet Burger', category: 'Signature', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800&q=80' },
    { title: 'Fresh Ingredients', category: 'Kitchen', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=800&q=80' },
    { title: "Chef's Plating", category: 'Kitchen', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80' },
    { title: 'Fine Dining Setup', category: 'Interior', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=800&q=80' },
    { title: 'T-Bone Steak', category: 'Grill', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=800&q=80' },
    { title: 'Pizza Special', category: 'Signature', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&q=80' },
    { title: 'Dessert Platter', category: 'Dessert', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=800&q=80' },
    { title: 'Rolex Wrap', category: 'Street Food', media_type: 'image', image_url: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=800&q=80' },
    // Example video entries (replace URLs with your hosted/embed links)
    { title: 'Steak on the Grill', category: 'Grill', media_type: 'video', image_url: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=800&q=80', video_url: 'https://www.youtube.com/watch?v=9KZi3JjN8fM' },
    { title: 'Chef in Action', category: 'Kitchen', media_type: 'video', image_url: 'https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=800&q=80', video_url: 'https://www.youtube.com/watch?v=9KZi3JjN8fM' },
  ];

  // Use API images if available, otherwise fallback
  const displayImages = images.length > 0 ? images : fallbackImages;

  const getImageUrl = (img) => img.image_url || img.url || '';
  const getTitle = (img) => img.title || 'Gallery Image';
  const getCategory = (img) => img.category || 'General';
  const getFilterKey = (img) => categoryMap[getCategory(img)] || 'food';
  const isVideo = (img) => (img.media_type || 'image') === 'video';
  const getVideoUrl = (img) => img.video_url || '';

  // Convert a YouTube/Instagram/TikTok/etc. watch link into an embeddable URL,
  // or detect a direct media file (.mp4/.webm/.ogg/.mov). Returns null if unknown.
  const toEmbedUrl = (url) => {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    if (url.includes('youtube.com/embed/')) return url;
    const tg = url.match(/tiktok\.com\/.+\/video\/(\d+)/);
    if (tg) return `https://www.tiktok.com/embed/v2/${tg[1]}`;
    const ig = url.match(/instagram\.com\/(?:p|reel|tv)\/([\w-]+)/);
    if (ig) return `https://www.instagram.com/p/${ig[1]}/embed`;
    if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)) return url;
    return null;
  };

  // Renders the visual for an item. In the grid it's muted/looping; in the lightbox controls are on.
  const renderMedia = (img, { controls = false } = {}) => {
    const poster = getImageUrl(img);
    const embed = toEmbedUrl(getVideoUrl(img));
    if (embed) {
      return (
        <iframe
          src={`${embed}${embed.includes('?') ? '&' : '?'}autoplay=${controls ? 1 : 0}&mute=1&loop=1`}
          title={getTitle(img)}
          style={{ height: '100%', width: '100%', border: 0, display: 'block', objectFit: 'cover' }}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      );
    }
    // Direct file: <video> with poster fallback
    return (
      <video
        src={getVideoUrl(img)}
        poster={poster}
        muted
        loop
        playsInline
        controls={controls}
        style={{ height: '100%', width: '100%', objectFit: 'cover', display: 'block', background: '#000' }}
      />
    );
  };

  const filteredImages = activeTab === 'all'
    ? displayImages
    : displayImages.filter(img => getFilterKey(img) === activeTab);

  const handleTabChange = (key) => {
    setAnimating(true);
    setTimeout(() => {
      setActiveTab(key);
      setTimeout(() => setAnimating(false), 50);
    }, 200);
  };

  // Lightbox keyboard navigation
  const navigateLightbox = useCallback((direction) => {
    if (!lightbox) return;
    const currentIndex = filteredImages.findIndex(img => img === lightbox);
    const nextIndex = direction === 'next'
      ? (currentIndex + 1) % filteredImages.length
      : (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setLightbox(filteredImages[nextIndex]);
  }, [lightbox, filteredImages]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightbox) return;
      if (e.key === 'ArrowRight') navigateLightbox('next');
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightbox, navigateLightbox]);

  return (
    <div>
      {/* Gallery Header */}
      <section className="section-padding text-center" style={{
        background: 'linear-gradient(rgba(26, 26, 26, 0.8), rgba(26, 26, 26, 0.9)), url("https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1920&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: '#ffffff'
      }}>
        <div className="container">
          <span className="section-subtitle" style={{ color: '#D4AF37' }}>Visual Journey</span>
          <h1 className="mt-3 mb-4" style={{ fontFamily: 'Playfair Display, serif', fontSize: '3.5rem', fontWeight: 600, color: '#ffffff' }}>
            Our Gallery
          </h1>
          <div className="gold-divider-center" style={{ background: '#D4AF37' }}></div>
          <p style={{ fontSize: '1.1rem', maxWidth: '600px', margin: '20px auto 0', lineHeight: 1.8, color: '#ffffff' }}>
            A feast for the eyes — explore our dishes, ambiance, and culinary artistry
          </p>
        </div>
      </section>

      {/* Category Filter Tabs */}
      <section style={{ backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 100 }}>
        <div className="container">
          <div className="gallery-filter-tabs d-flex justify-content-center gap-2 py-3 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat.key}
                onClick={() => handleTabChange(cat.key)}
                className={`gallery-filter-tab ${activeTab === cat.key ? 'active' : ''}`}
                data-track={`filter-${cat.key}`}
              >
                <span>{cat.icon}</span> {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding" style={{ backgroundColor: '#faf8f5' }}>
        <div className="container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTopColor: '#D4AF37', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }}></div>
              <p style={{ color: '#999' }}>Loading gallery...</p>
            </div>
          ) : (
            <>
              <p className="text-center mb-4" style={{ color: '#999', fontSize: '0.85rem' }}>
                Showing {filteredImages.length} {filteredImages.length === 1 ? 'image' : 'images'}
              </p>
              <div className={`row g-3 gallery-grid ${animating ? 'fading' : ''}`}>
                {filteredImages.map((image, index) => (
                <div key={index} className="col-md-6 col-lg-4">
                  <div
                    className="img-zoom hover-lift"
                    style={{
                      borderRadius: '16px',
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: 'pointer',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      height: '280px',
                      backgroundColor: '#000'
                    }}
                    onClick={() => setLightbox(image)}
                  >
                    {isVideo(image) ? (
                      <>
                        {renderMedia(image)}
                        <div style={{
                          position: 'absolute', top: '12px', left: '12px',
                          display: 'flex', alignItems: 'center', gap: '5px',
                          backgroundColor: 'rgba(212, 175, 55, 0.92)', color: '#1A1A1A',
                          padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600
                        }}>
                          <PlayCircle size={13} /> Video
                        </div>
                      </>
                    ) : (
                      <img
                        src={getImageUrl(image)}
                        alt={getTitle(image)}
                        style={{ height: '100%', objectFit: 'cover', width: '100%', display: 'block' }}
                      />
                    )}
                    {/* Overlay */}
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(transparent 40%, rgba(0,0,0,0.75))',
                      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                      padding: '24px',
                      opacity: 0,
                      transition: 'opacity 0.3s ease'
                    }}
                      className="gallery-overlay"
                      onMouseOver={e => e.currentTarget.style.opacity = 1}
                      onMouseOut={e => e.currentTarget.style.opacity = 0}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <span style={{
                            color: '#D4AF37', fontSize: '0.7rem', letterSpacing: '2px',
                            textTransform: 'uppercase', fontWeight: 600
                          }}>
                            {getCategory(image)}
                          </span>
                          <h5 className="text-white mb-0" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem' }}>
                            {getTitle(image)}
                          </h5>
                        </div>
                        <div style={{
                          width: '40px', height: '40px', borderRadius: '50%',
                          backgroundColor: 'rgba(212, 175, 55, 0.3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <ZoomIn size={18} color="#ffffff" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              </div>
              {filteredImages.length === 0 && (
                <div className="text-center py-5">
                  <ImageIcon size={48} color="#ddd" />
                  <p className="mt-3" style={{ color: '#999' }}>No images in this category yet.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Social Media Section */}
      <section className="section-padding-sm text-center" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="container">
          <h2 className="text-white mb-3" style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', fontWeight: 600 }}>
            Follow Our Journey
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', maxWidth: '500px', margin: '0 auto 30px', fontSize: '0.95rem', lineHeight: 1.8 }}>
            Stay connected for daily updates, special offers, and behind-the-scenes moments
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <a href="https://www.facebook.com/syrafoodpoint" target="_blank" rel="noopener noreferrer" className="social-icon"><i className="fab fa-facebook-f"></i></a>
            <a href="https://www.instagram.com/syrafoodpoint" target="_blank" rel="noopener noreferrer" className="social-icon"><i className="fab fa-instagram"></i></a>
            <a href="https://www.tiktok.com/@syrafoodpoint" target="_blank" rel="noopener noreferrer" className="social-icon"><i className="fab fa-tiktok"></i></a>
            <a href="https://wa.me/256772206129" target="_blank" rel="noopener noreferrer" className="social-icon"><i className="fab fa-whatsapp"></i></a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.95)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '40px'
          }}
          onClick={() => setLightbox(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: 'absolute', top: '20px', right: '20px',
              background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
              cursor: 'pointer', width: '44px', height: '44px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 10
            }}
            aria-label="Close lightbox"
          >
            <X size={24} />
          </button>

          {/* Previous button */}
          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
            style={{
              position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
              cursor: 'pointer', width: '48px', height: '48px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s', zIndex: 10
            }}
            aria-label="Previous image"
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <ChevronLeft size={24} />
          </button>

          {/* Main image */}
          {isVideo(lightbox) ? (
            <div style={{
              width: '85%', maxWidth: '900px', maxHeight: '80vh',
              borderRadius: '12px', overflow: 'hidden',
              boxShadow: '0 10px 50px rgba(0,0,0,0.5)', backgroundColor: '#000',
              aspectRatio: '16 / 9'
            }}
            onClick={e => e.stopPropagation()}>
              {renderMedia(lightbox, { controls: true })}
            </div>
          ) : (
            <img
              src={getImageUrl(lightbox)}
              alt={getTitle(lightbox)}
              style={{
                maxWidth: '85%', maxHeight: '80vh',
                objectFit: 'contain', borderRadius: '12px',
                boxShadow: '0 10px 50px rgba(0,0,0,0.5)'
              }}
              onClick={e => e.stopPropagation()}
            />
          )}

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
            style={{
              position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
              cursor: 'pointer', width: '48px', height: '48px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s', zIndex: 10
            }}
            aria-label="Next image"
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <ChevronRight size={24} />
          </button>

          {/* Caption */}
          <div style={{
            position: 'absolute', bottom: '30px',
            textAlign: 'center', color: '#fff', left: '50%', transform: 'translateX(-50%)'
          }}>
            <h5 style={{ fontFamily: 'Playfair Display, serif', color: '#fff', marginBottom: '4px' }}>{getTitle(lightbox)}</h5>
            <span style={{ color: '#D4AF37', fontSize: '0.8rem', letterSpacing: '2px', textTransform: 'uppercase' }}>{getCategory(lightbox)}</span>
            <div style={{ marginTop: '8px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
              Use ← → arrow keys to navigate • Esc to close
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Gallery;
